import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ReferenceArea, CartesianGrid } from "recharts";
import { Lightbulb } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, PlusGate } from "@/components/Common";

const LINES = [
  { key: "mood", label: "Mood", color: "#C86D51" },
  { key: "energy", label: "Energy", color: "#6E8B74" },
  { key: "stress", label: "Stress", color: "#5A3E4E" },
];

const phaseBands = (series) => {
  const bands = [];
  series.forEach((p, i) => {
    const last = bands[bands.length - 1];
    if (p.phase && last && last.phase === p.phase && last.end === i - 1) last.end = i;
    else if (p.phase) bands.push({ phase: p.phase, start: i, end: i });
  });
  return bands.map((b) => ({ ...b, x1: series[b.start].label, x2: series[b.end].label }));
};

const PHASE = {
  menstrual: { label: "Menstrual", color: "#D9776C" },
  follicular: { label: "Follicular", color: "#6E9F85" },
  ovulatory: { label: "Ovulatory", color: "#E6B057" },
  luteal: { label: "Luteal", color: "#9B84A1" },
};

function RangeBar({ days, setDays, plus, cycle, overlay, setOverlay }) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <div className="inline-flex p-1 rounded-full bg-[#EFECE6]">
        {[7, 30].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`trends-range-${d}`}
            className={`chip min-h-11 px-4 rounded-full text-sm font-medium ${days === d ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>
            {d} days{d === 30 && !plus ? " · Plus" : ""}
          </button>
        ))}
      </div>
      {cycle && (
        <label className="min-h-11 flex items-center gap-2 px-1 cursor-pointer text-sm text-stone-600">Cycle overlay
          <Switch data-testid="trends-cycle-overlay-switch" checked={overlay} onCheckedChange={setOverlay} /></label>
      )}
    </div>
  );
}

function Patterns({ data }) {
  if (data?.patterns_locked > 0) return <PlusGate testid="trends-patterns-gate" title={`CAMRYN found ${data.patterns_locked} pattern${data.patterns_locked > 1 ? "s" : ""} in your data`} body="Plus explains what links your sleep, stress, movement and water to how you feel, with the numbers behind each pattern." />;
  if (!data?.patterns?.length) return <p className="text-sm text-stone-500 card p-4" data-testid="trends-no-patterns">No clear patterns yet. Most become visible after 10–14 check-ins.</p>;
  return (
    <div className="space-y-2.5" data-testid="trends-patterns-list">
      {data.patterns.map((p) => (
        <div key={p.id} className="card p-4 flex gap-3">
          <Lightbulb className="h-5 w-5 text-[#D99B52] shrink-0 mt-0.5" />
          <div><p className="text-sm text-stone-800">{p.text}</p><p className="text-[11px] text-stone-400 mt-1">Based on {p.based_on} days · a link, not proof of cause</p></div>
        </div>
      ))}
    </div>
  );
}

export default function Trends({ embedded }) {
  const { user } = useAuth();
  const plus = user.plan === "plus";
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [overlay, setOverlay] = useState(true);

  useEffect(() => {
    if (days === 30 && !plus) return;
    api.get(`/trends?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
  }, [days, plus]);

  return (
    <div>
      {embedded ? <h2 className="font-display text-xl mb-1">How you've been</h2> : <PageTitle eyebrow="Trends" title="How you've been" sub="Mood, energy and stress (1–5) with sleep hours as bars." />}
      {embedded && <p className="text-xs text-stone-500 mb-4">From your energy checks and anything you log in more detail. Mood and stress need "Log more".</p>}
      <RangeBar days={days} setDays={setDays} plus={plus} cycle={user.cycle_tracking} overlay={overlay} setOverlay={setOverlay} />
      {days === 30 && !plus ? (
        <PlusGate testid="trends-plus-gate" title="See the full month" body="30-day trends show how your cycle, sleep and stress shape the whole month, not just this week." />
      ) : (
        <>
          <TrendsChart data={data} days={days} overlay={overlay} cycle={user.cycle_tracking} />
          <div className="mt-8 rise d2">
            <div className="eyebrow mb-3">Patterns in your data</div>
            <Patterns data={data} />
          </div>
        </>
      )}
    </div>
  );
}

function TrendsChart({ data, days, overlay, cycle }) {
  const series = (data?.series || []).map((p) => ({ ...p, label: new Date(p.date + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) }));
  return (
          <div className="card p-4 sm:p-5 rise" data-testid="trends-chart">
            <div className="h-72">
              <ResponsiveContainer width="100%" height={288} minWidth={200}>
                <ComposedChart data={series} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#EFECE6" />
                  {overlay && phaseBands(series).map((b) => (
                    <ReferenceArea key={`${b.phase}-${b.start}`} x1={b.x1} x2={b.x2} yAxisId="s" fill={PHASE[b.phase].color} fillOpacity={0.12} stroke="none" />
                  ))}
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#989087" }} tickLine={false} axisLine={false} interval={days === 30 ? 4 : 0} />
                  <YAxis yAxisId="s" domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11, fill: "#989087" }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="h" orientation="right" domain={[0, 12]} hide />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E1D8", fontSize: 12 }} />
                  <Bar yAxisId="h" dataKey="sleep_hours" name="Sleep (h)" fill="#E8DCCB" radius={[6, 6, 0, 0]} barSize={days === 30 ? 6 : 18} />
                  {LINES.map((l) => (
                    <Line key={l.key} yAxisId="s" type="monotone" dataKey={l.key} name={l.label} stroke={l.color} strokeWidth={2.25} dot={{ r: 2.5 }} connectNulls />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-stone-600">
              {LINES.map((l) => <span key={l.key} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: l.color }} />{l.label}</span>)}
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#E8DCCB]" />Sleep hours</span>
              {overlay && cycle && Object.values(PHASE).map((p) => (
                <span key={p.label} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: p.color, opacity: 0.5 }} />{p.label}</span>
              ))}
            </div>
          </div>
  );
}
